import * as React from 'react'
import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { ThemeProps, withTheme } from 'styled-components'
import Dropzone from 'react-dropzone'
import {
  displayToast,
  DisplayToastDispatch,
  DocumentRecord,
  GoverningDocumentsCategoryUrlProps,
  Organization,
  PageProps,
  Paths,
  setExpanded,
} from '@logic'
import { withLoadingCachedMultiple } from '@components/loading'
import { loadPermissions, PermissionProps } from '@components/permissions'
import { withPermissions } from '@shared/hocs/with-permissions'
import { BuiltInPermission } from '@shared/helpers/constants'
import { PageContent } from '@components/pages'
import { Tabs, Tab } from '@shared/components/tabs/tabs'
import { ActionsDrop } from '@shared/components/actionsDrop'
import { ProgressBar } from '@components/progress'
import { withService } from '@src/shared/hocs/with-service'
import { NavigationProps, withNavigation } from '@components/navigation/navigation'
import { CommonInput, FlexRow } from '@src/styles'
import FileUploadIcon from '@image/file-upload.svg'
import {
  AgreementCategory,
  categoryInfo,
  categoryList,
  getCategoryFromPath,
  getStoragePath,
} from './utils'
import { AgreementsTable } from './components/agreements-table'
import { newDeleteDocumentModal } from './components/modals'
import {
  PageWrapper,
  ContentWrapper,
  CategoryDescription,
  LabelButton,
} from './style'

declare let API_URL: string

type BaseProps = PageProps & PermissionProps

interface Props extends NavigationProps, BaseProps {
  displayToast: DisplayToastDispatch
  setExpanded: any
}

interface StateProps {
  currentOrganization: Organization
}

const withData = withLoadingCachedMultiple<Props>(loadPermissions) as any

// Build tabs for category navigation
const buildTabs = (organizationId: string): Tab[] => {
  return categoryList.map((cat) => ({
    name: cat.label,
    destination:
      cat.key === AgreementCategory.all
        ? Paths.governingDocuments
        : `${Paths.governingDocuments}/${cat.key}`,
    exact: cat.key === AgreementCategory.all,
  }))
}

const Agreements = withTheme(
  withData(
    withPermissions([BuiltInPermission.viewGoverningDocuments])(
      (props: Props & StateProps & ThemeProps<any>) => {
        const { currentOrganization, serviceOld, permissions, reload, params, setExpanded } =
          props
        const categoryParam = (params as GoverningDocumentsCategoryUrlProps).category
        const currentCategory = getCategoryFromPath(categoryParam)
        const categoryData = categoryInfo[currentCategory]
        const isLegacyDataRoom = !!currentOrganization?.legacyDataRoom

        const [documents, setDocuments] = useState<DocumentRecord[] | undefined>(undefined)
        const [uploadPercentageComplete, setUploadPercentageComplete] = useState<
          number | undefined
        >(undefined)
        const [loaded, setLoaded] = useState<boolean>(false)
        const [path, setPath] = useState<string[]>([])
        const [deleteTarget, setDeleteTarget] = useState<DocumentRecord | null>(null)

        const canEditDocument = !!permissions.find(
          (permission) => permission.id === BuiltInPermission.editDocuments
        )
        const canDeleteDocument = !!permissions.find(
          (permission) => permission.id === BuiltInPermission.deleteDocuments
        )

        const getStoragePathForCategory = (): string => {
          const basePath = getStoragePath(currentCategory)
          if (path.length > 0) {
            return basePath + '/' + path.join('/')
          }
          return basePath
        }

        const fullPath = encodeURIComponent('/' + getStoragePathForCategory())

        const handleDelete = async () => {
          if (deleteTarget && currentOrganization) {
            const docIsFolder = isLegacyDataRoom ? !!deleteTarget.folder : deleteTarget.type === 2
            const name = deleteTarget.name || ''
            const fileExtension = name.split('.').slice(-1)
            const nameWithExtension = docIsFolder || name.split('.').slice(-1)[0] === fileExtension[0]
              ? name
              : name.concat('.' + fileExtension)
            const docFullPath = '/' + getStoragePathForCategory() + '/' + nameWithExtension

            await serviceOld.deleteDocuments(currentOrganization.id, deleteTarget.id, docFullPath)
            deleteModal.setVisible(false)
            setDeleteTarget(null)
            await fetchDocuments()
            reload()
          }
        }

        const deleteModal = newDeleteDocumentModal(
          handleDelete,
          deleteTarget?.name
        )

        const fetchDocuments = async () => {
          setLoaded(false)
          if (currentOrganization) {
            try {
              const records = await serviceOld.getOrganizationDocuments(
                currentOrganization.id,
                fullPath
              )
              const recordsValue = records?.map((r: DocumentRecord) => {
                if (r.folder) {
                  return {
                    ...r,
                    items:
                      r.folder.childCount === 1
                        ? `${r.folder.childCount} item`
                        : `${r.folder.childCount} items`,
                  }
                }
                return r
              })
              setDocuments(recordsValue || [])
            } catch (error) {
              setDocuments([])
            }
            setLoaded(true)
          }
        }

        useEffect(() => {
          setPath([])
        }, [currentCategory])

        useEffect(() => {
          let mounted = true
          ;(async () => {
            if (mounted) {
              await fetchDocuments()
            }
          })()
          return () => {
            mounted = false
          }
        }, [currentCategory, path, currentOrganization?.id])

        const uploadDocuments = async (files: any, targetCategory?: AgreementCategory) => {
          const uploadCategory = targetCategory || currentCategory
          const uploadBasePath =
            uploadCategory === AgreementCategory.all
              ? 'governing/formation'
              : getStoragePath(uploadCategory)
          const uploadPath = encodeURIComponent('/' + uploadBasePath + (path.length > 0 ? '/' + path.join('/') : ''))

          const onUploadProgress = (event: any) => {
            if (event.lengthComputable) {
              const percentages = +((event.loaded / event.total) * 100).toFixed(2)
              setUploadPercentageComplete(percentages)
            }
          }
          await serviceOld.uploadOrganizationDocuments(
            files,
            currentOrganization.id,
            uploadPath,
            onUploadProgress
          )
          setUploadPercentageComplete(undefined)
          setExpanded()
          await fetchDocuments()
          reload()
        }

        const handleView = (doc: DocumentRecord) => {
          const docIsFolder = isLegacyDataRoom ? !!doc.folder : doc.type === 2
          if (docIsFolder) return

          const name = doc.name || ''
          const fileExtension = name.split('.').slice(-1)
          const nameWithExtension = name.split('.').slice(-1)[0] === fileExtension[0]
            ? name
            : name.concat('.' + fileExtension)
          const docFullPath = '/' + getStoragePathForCategory() + '/' + nameWithExtension
          const docxRegex = /\.docx$/
          const queryString = docxRegex.test(name)
            ? `?format=pdf&fullPath=${docFullPath}`
            : `?fullPath=${docFullPath}`

          window.open(
            `${API_URL}/organization/${currentOrganization.id}/document/${doc.id}/content/${name}${queryString}`,
            '_blank'
          )
        }

        const handleDeleteClick = (doc: DocumentRecord) => {
          setDeleteTarget(doc)
          deleteModal.setVisible(true)
        }

        const handleFolderClick = (doc: DocumentRecord) => {
          const folderName = isLegacyDataRoom ? doc.folder?.name : doc.name
          if (folderName) {
            setPath([...path, folderName])
          }
        }

        const dropOptions = () => {
          if (!canEditDocument) return []

          const uploadFile = () => {
            return (
              <Dropzone
                onDrop={async (files: any) => {
                  return uploadDocuments(files)
                }}
              >
                {({ getInputProps }) => (
                  <LabelButton>
                    <CommonInput {...getInputProps()} />
                    <FileUploadIcon />
                    <span>Upload Document</span>
                  </LabelButton>
                )}
              </Dropzone>
            )
          }

          return [[uploadFile(), () => { /* noop */ }]]
        }

        const tabs = buildTabs(currentOrganization?.id)

        const renderBreadcrumbs = () => {
          if (path.length === 0) return null

          return (
            <div style={{ marginBottom: '16px' }}>
              <span
                style={{ cursor: 'pointer', color: '#33AA40' }}
                onClick={() => setPath([])}
              >
                {categoryData.label}
              </span>
              {path.map((p, i) => (
                <span key={i}>
                  <span style={{ margin: '0 8px', color: '#666' }}>/</span>
                  <span
                    style={{
                      cursor: i < path.length - 1 ? 'pointer' : 'default',
                      color: i < path.length - 1 ? '#33AA40' : '#fff',
                    }}
                    onClick={() => {
                      if (i < path.length - 1) {
                        setPath(path.slice(0, i + 1))
                      }
                    }}
                  >
                    {p}
                  </span>
                </span>
              ))}
            </div>
          )
        }

        return (
          <PageWrapper>
            {deleteModal.component}
            <Tabs tabs={tabs} title="Agreements" params={params} />
            <PageContent>
              <ContentWrapper>
                <FlexRow
                  justifyContent="space-between"
                  alignItems="center"
                  style={{ width: '100%', marginBottom: '8px' }}
                >
                  <div>
                    {renderBreadcrumbs()}
                    <CategoryDescription>{categoryData.description}</CategoryDescription>
                  </div>
                  <ActionsDrop options={dropOptions()} />
                </FlexRow>

                <ProgressBar percentage={uploadPercentageComplete} />

                {!loaded ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    Loading documents...
                  </div>
                ) : (
                  <AgreementsTable
                    documents={documents || []}
                    category={currentCategory}
                    onView={handleView}
                    onDelete={handleDeleteClick}
                    onFolderClick={handleFolderClick}
                    canEdit={canEditDocument}
                    canDelete={canDeleteDocument}
                    isLegacyDataRoom={isLegacyDataRoom}
                  />
                )}
              </ContentWrapper>
            </PageContent>
          </PageWrapper>
        )
      }
    )
  )
)

const mapStateToProps = (state: any) => {
  return {
    currentOrganization: state.organization!.currentOrganization,
  }
}

// Lazy init to avoid webpack scope-hoisting TDZ crash with withService
let _AgreementsPage: any = null
export const AgreementsPage: React.ComponentType<any> = (props: any) => {
  if (!_AgreementsPage) _AgreementsPage = withNavigation(connect(mapStateToProps, { displayToast, setExpanded })(withService(Agreements)))
  return React.createElement(_AgreementsPage, props)
}
